// src/pages/HomePage.js — Premium Hero: 3D crypto coins · mouse parallax · rich dashboard
import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaGooglePlay,
  FaApple,
  FaBrain,
  FaBitcoin,
  FaBell,
  FaMobileAlt,
  FaCheckCircle,
  FaChevronDown,
  FaUsers,
  FaLightbulb,
  FaShieldAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import {
  FiTrendingUp,
  FiBarChart2,
  FiTarget,
  FiSmartphone,
  FiGlobe,
  FiZap,
  FiArrowRight,
} from 'react-icons/fi';
import {
  SiBitcoin,
  SiEthereum,
  SiSolana,
  SiBinance,
  SiCardano,
  SiRipple,
} from 'react-icons/si';

import ClosedSignalsTicker from '../components/ClosedSignalsTicker';
import ActiveSignalsTicker from '../components/ActiveSignalsTicker';
import LiveResultsBanner from '../components/LiveResultsBanner';
import TokenTicker from '../components/TokenTicker';
import { APP_LINKS } from '../utils/constants';

/* ─────────────────────────────────────────────────────────
   COLOUR CONSTANTS
───────────────────────────────────────────────────────── */
const N  = '#0d2235';
const E  = '#29ABE2';
const Eo = '#1a8fc0';
const OR = '#F7941D';
const FG = '#f0f9ff';
const PA = '#ffffff';
const BD = '#1f3446';
const MT = '#5a7a8f';
const BR = 'rgba(13,34,53,0.11)';
const BM = 'rgba(13,34,53,0.20)';
const SL = 'rgba(0,0,0,0.08) 0px 6px 20px 0px';

/* ─────────────────────────────────────────────────────────
   KEYFRAMES
───────────────────────────────────────────────────────── */
const orbPulse = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(2.8); opacity: 0;   }
`;
const scanPing = keyframes`
  0%   { transform: scale(0.4); opacity: 0.7; }
  100% { transform: scale(2.4); opacity: 0;   }
`;
const particleDrift = keyframes`
  0%   { transform: translateY(0);     opacity: 0;   }
  12%  { opacity: 0.8; }
  88%  { opacity: 0.8; }
  100% { transform: translateY(-64px); opacity: 0;   }
`;
const orbitCW = keyframes`
  from { transform: rotate(0deg)   translateX(118px) rotate(0deg); }
  to   { transform: rotate(360deg) translateX(118px) rotate(-360deg); }
`;
const orbitCCW = keyframes`
  from { transform: rotate(0deg)    translateX(118px) rotate(0deg); }
  to   { transform: rotate(-360deg) translateX(118px) rotate(360deg); }
`;
const glowBreath = keyframes`
  0%,100% { transform: translate(-50%,-50%) scale(1);    opacity: 0.22; }
  50%      { transform: translate(-50%,-50%) scale(1.28); opacity: 0.34; }
`;
/* ─────────────────────────────────────────────────────────
   SHARED PRIMITIVES — Wise design language
───────────────────────────────────────────────────────── */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 6px 14px;
  border: 1.5px solid ${BM};
  border-radius: 9999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${BD};
  background: ${FG};
  margin-bottom: 1rem;
`;

const DarkBadge = styled(Badge)`
  background: rgba(41,171,226,0.14);
  border-color: rgba(41,171,226,0.28);
  color: ${E};
`;

const DisplayH1 = styled.h1`
  font-size: clamp(1.8rem, 5.2vw, 3.5rem) !important;
  font-weight: 800;
  line-height: 1.08 !important;
  letter-spacing: -0.01em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 1.4rem;

  em {
    font-style: normal;
    color: ${E} !important;
    -webkit-text-fill-color: ${E} !important;
  }

  .hi {
    display: inline;
    vertical-align: middle;
    position: relative;
    top: -3px;
  }

  @media (max-width: 600px) {
    font-size: clamp(1.6rem, 8vw, 2.4rem) !important;
    line-height: 1.1 !important;
  }
`;

const DisplayH2 = styled.h2`
  font-size: clamp(2.2rem, 5vw, 4.5rem) !important;
  font-weight: 800;
  line-height: 0.93 !important;
  letter-spacing: -0.022em;
  margin: 0 0 1.1rem;

  em { font-style: normal; }

  @media (max-width: 600px) {
    font-size: clamp(1.9rem, 8.5vw, 2.8rem) !important;
    line-height: 0.97 !important;
  }
`;

const SectionH2 = styled.h2`
  font-size: clamp(1.8rem, 3.8vw, 3.2rem) !important;
  font-weight: 700;
  line-height: 1.05 !important;
  letter-spacing: -0.018em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 0.9rem;

  em {
    font-style: normal;
    color: ${E} !important;
    -webkit-text-fill-color: ${E} !important;
  }
`;

const SubCopy = styled.p`
  font-size: 1.06rem;
  color: ${MT} !important;
  line-height: 1.72;
  margin: 0;
`;

const PillBase = css`
  display: inline-flex;
  align-items: center;
  gap: 0.62rem;
  padding: 13px 26px;
  border-radius: 9999px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition: transform 0.14s ease, opacity 0.14s ease, background 0.14s ease;
  svg { font-size: 1.3rem; flex-shrink: 0; }
  .bt { display:flex; flex-direction:column; align-items:flex-start; line-height:1.22; }
  .bs { font-size:0.62rem; font-weight:400; opacity:0.82; }
  .bm { font-size:0.87rem; font-weight:700; }
  &:hover  { transform:translateY(-1px); opacity:0.9; }
  &:active { transform:translateY(0);    opacity:1;   }
`;

const PrimaryPill = styled.a`${PillBase}
  background: ${E};
  color: ${PA};
`;

const OutlinePill = styled.a`${PillBase}
  background: ${PA};
  color: ${N};
  border: 1.5px solid ${BM};
  &:hover { background: ${FG}; opacity:1; }
`;

const WhitePill = styled.a`${PillBase}
  background: ${PA};
  color: ${N};
`;

const GhostPill = styled.a`${PillBase}
  background: rgba(255,255,255,0.08);
  color: ${PA};
  border: 1.5px solid rgba(255,255,255,0.26);
  &:hover { background:rgba(255,255,255,0.16); opacity:1; }
`;

const fUp = {
  hidden:  { opacity:0, y:28 },
  visible: (i=0) => ({
    opacity:1, y:0,
    transition:{ delay:i*0.15, duration:0.6, ease:[0.22, 1, 0.36, 1] },
  }),
};

/* ═══════════════════════════════════════════════════════
   ①  HERO
═══════════════════════════════════════════════════════ */

const HeroWrap = styled.section`
  background:
    radial-gradient(rgba(13,34,53,0.065) 1px, transparent 1px),
    radial-gradient(ellipse at 68% 38%, rgba(41,171,226,0.11) 0%, transparent 50%),
    radial-gradient(ellipse at 14% 74%, rgba(247,148,29,0.08) 0%, transparent 42%),
    linear-gradient(180deg, #eef6ff 0%, #f5f9ff 55%, #ffffff 100%) !important;
  background-size: 28px 28px, 100% 100%, 100% 100%, 100% 100% !important;
  background-color: #f5f9ff !important;
  min-height: 100vh;
  padding: 0 10% !important;
  overflow: hidden;
  position: relative;

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 900px) { padding: 0 5% !important; }
`;

const HeroInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  min-height: 100vh;
  padding: clamp(80px, 10vw, 100px) 0 60px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    min-height: auto;
    padding: clamp(90px, 14vw, 120px) 0 60px;
    text-align: center;
    gap: 2.5rem;
  }
`;

const HeroLeft = styled.div``;

const HeroSub = styled.p`
  font-size: 1.04rem;
  color: ${MT} !important;
  line-height: 1.74;
  max-width: 480px;
  margin: 0 0 2.2rem;
  @media (max-width: 960px) { margin: 0 auto 2.2rem; }
`;

const HeroBtnRow = styled.div`
  display: flex;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-bottom: 1.7rem;
  @media (max-width: 960px) { justify-content: center; }
`;

const HeroTrust = styled.div`
  display: flex;
  align-items: center;
  gap: 1.3rem;
  flex-wrap: wrap;
  font-size: 13px;
  color: ${MT};
  @media (max-width: 960px) { justify-content: center; }
  .ti {
    display: flex; align-items: center; gap: 0.35rem;
    svg { color: ${E}; font-size: 0.88rem; }
  }
`;

/* ─── Visual canvas ─── */

const VisualWrap = styled.div`
  position: relative;
  width: 100%;
  min-height: 660px;
  isolation: isolate;
  perspective: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 960px) { min-height: 460px; }
`;

/* Glow blobs */
const BgGlowBlue = styled.div`
  position: absolute;
  width: 460px; height: 460px;
  top: 50%; left: 46%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(41,171,226,0.16) 0%, transparent 65%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
`;

const BgGlowOrange = styled.div`
  position: absolute;
  width: 260px; height: 260px;
  top: -8%; right: 2%;
  background: radial-gradient(circle, rgba(247,148,29,0.18) 0%, transparent 65%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
`;

const BgGlowPurple = styled.div`
  position: absolute;
  width: 220px; height: 220px;
  bottom: 6%; right: 4%;
  background: radial-gradient(circle, rgba(153,69,255,0.14) 0%, transparent 65%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
`;

const BgGlowIndigo = styled.div`
  position: absolute;
  width: 180px; height: 180px;
  top: 10%; left: 0%;
  background: radial-gradient(circle, rgba(98,126,234,0.14) 0%, transparent 65%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
`;

/* 3D Coin element */
const Coin3D = styled(motion.div)`
  width: ${p => p.$s}px;
  height: ${p => p.$s}px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 23%, rgba(255,255,255,0.48) 0%, rgba(255,255,255,0.20) 16%, transparent 31%),
    ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,0.34);
  box-shadow:
    0 10px 30px ${p => p.$glow || 'rgba(41,171,226,0.35)'},
    0 3px 10px rgba(0,0,0,0.18),
    inset 0 2px 0 rgba(255,255,255,0.32),
    inset 0 -3px 0 rgba(0,0,0,0.20);
  will-change: transform;
  overflow: hidden;
  position: relative;
  transform-style: preserve-3d;

  &::before {
    content: '';
    position: absolute;
    inset: 6px;
    border-radius: inherit;
    border: 1px solid rgba(255,255,255,0.16);
    z-index: 1;
    pointer-events: none;
  }

  svg {
    position: relative;
    z-index: 2;
    color: rgba(255,255,255,0.94);
    width: 1em;
    height: 1em;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.28));
  }

`;

/* Scan rings (AI scanning visual) */
const ScanRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(41,171,226,${p => p.$op || 0.35});
  width: ${p => p.$sz || 80}px;
  height: ${p => p.$sz || 80}px;
  animation: ${scanPing} ${p => p.$dur || 3}s ease-out ${p => p.$delay || 0}s infinite;
`;

/* Data particle */
const Particle = styled.div`
  position: absolute;
  width: ${p => p.$w || 4}px;
  height: ${p => p.$w || 4}px;
  border-radius: 50%;
  background: ${p => p.$c || E};
  z-index: 2;
  pointer-events: none;
  animation: ${particleDrift} ${p => p.$dur || 4}s ease-in-out ${p => p.$delay || 0}s infinite;
  opacity: 0;
`;

/* ─── Coin data (live prices + real images) ─── */

const COIN_IMGS = {
  BTC: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
  ETH: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
  SOL: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
  BNB: 'https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png',
  ADA: 'https://assets.coingecko.com/coins/images/975/large/cardano.png',
  XRP: 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png',
};

const COINS = [
  { sym:'BTC', name:'Bitcoin',  color:'#F7931A', dark:'#b85e0a', glow:'rgba(247,147,26,0.75)',  icon:SiBitcoin, iconSize:94 },
  { sym:'ETH', name:'Ethereum', color:'#627EEA', dark:'#3a52c8', glow:'rgba(98,126,234,0.75)',  icon:SiEthereum, iconSize:82 },
  { sym:'SOL', name:'Solana',   color:'#9945FF', dark:'#13F195', glow:'rgba(153,69,255,0.75)',  icon:SiSolana, iconSize:88 },
  { sym:'BNB', name:'BNB',      color:'#F0B90B', dark:'#c08f08', glow:'rgba(240,185,11,0.75)',  icon:SiBinance, iconSize:88 },
  { sym:'ADA', name:'Cardano',  color:'#0033AD', dark:'#002285', glow:'rgba(0,51,173,0.75)',    icon:SiCardano, iconSize:88 },
  { sym:'XRP', name:'XRP',      color:'#00AAE4', dark:'#0086b8', glow:'rgba(0,170,228,0.72)',   icon:SiRipple, iconSize:88 },
];

const DEFAULT_PRICES = {
  BTC:{ price:67234.52, change:2.41 },
  ETH:{ price:3142.18,  change:1.83 },
  SOL:{ price:168.45,   change:3.12 },
  BNB:{ price:606.23,   change:-1.71 },
  ADA:{ price:0.4832,   change:0.89 },
  XRP:{ price:0.6234,   change:1.24 },
};

function fmtPrice(price) {
  if (!price) return '—';
  if (price < 1)   return '$' + price.toFixed(4);
  if (price < 100) return '$' + price.toFixed(2);
  return '$' + price.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });
}

function glowAlpha(glow, alpha) {
  return glow.replace(/,\s*[\d.]+\)$/, `,${alpha})`);
}

function useLivePrices() {
  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const wsRef = useRef(null);
  const rtRef = useRef(null);

  useEffect(() => {
    function connect() {
      try {
        const streams = COINS.map(c => `${c.sym.toLowerCase()}usdt@miniTicker`).join('/');
        const sock = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
        wsRef.current = sock;

        sock.onmessage = (evt) => {
          try {
            const { data: d } = JSON.parse(evt.data);
            const sym    = d.s.replace('USDT', '');
            const price  = parseFloat(d.c);
            const open   = parseFloat(d.o);
            const change = open ? ((price - open) / open) * 100 : 0;
            setPrices(prev => ({ ...prev, [sym]: { price, change } }));
          } catch {}
        };
        sock.onclose  = () => { rtRef.current = setTimeout(connect, 5000); };
        sock.onerror  = () => { try { sock.close(); } catch {} };
      } catch {}
    }
    connect();
    return () => {
      wsRef.current?.close();
      clearTimeout(rtRef.current);
    };
  }, []);

  return prices;
}

/* Styled components for the premium carousel */

const CarouselStage = styled.div`
  position: relative;
  width: 250px; height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GlowBurst = styled.div`
  position: absolute;
  width: 340px; height: 340px;
  border-radius: 50%;
  background: radial-gradient(circle, ${p => p.$c} 0%, transparent 60%);
  top: 50%; left: 50%;
  animation: ${glowBreath} 2.8s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
  transition: background 0.7s ease;
`;

const OrbitRingEl = styled.div`
  position: absolute;
  top: 50%; left: 50%;
  width: 1px; height: 1px;
  pointer-events: none;
`;

const OrbitDot = styled.div`
  position: absolute;
  border-radius: 50%;
  width: ${p => p.$sz}px;
  height: ${p => p.$sz}px;
  background: ${p => p.$c};
  top: ${p => -(p.$sz/2)}px;
  left: ${p => -(p.$sz/2)}px;
  animation: ${p => p.$cw ? orbitCW : orbitCCW} ${p => p.$dur}s linear ${p => p.$dl}s infinite;
  opacity: ${p => p.$op};
  box-shadow: 0 0 ${p => p.$sz*2}px ${p => p.$c};
  transition: background 0.7s ease;
`;

const BigCoinEl = styled(motion.div)`
  width: 190px; height: 190px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 24%, rgba(255,255,255,0.56) 0%, rgba(255,255,255,0.24) 14%, transparent 30%),
    ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 30px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.38);
  outline: 8px solid rgba(255,255,255,0.10);
  box-shadow:
    0 0 70px  ${p => p.$glow},
    0 0 120px ${p => p.$glowSoft},
    0 28px 68px rgba(13,34,53,0.30),
    inset 0 3px 0 rgba(255,255,255,0.40),
    inset 0 -5px 0 rgba(0,0,0,0.20);
  z-index: 5;
  transform-style: preserve-3d;

  &::before {
    content: '';
    position: absolute;
    inset: 10px;
    border-radius: inherit;
    border: 1px solid rgba(255,255,255,0.18);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(160deg, rgba(255,255,255,0.14) 0%, transparent 55%);
    z-index: 1;
  }

  .ci { position:relative; z-index:2; color:rgba(255,255,255,0.97); width:1em; height:1em; filter:drop-shadow(0 4px 8px rgba(0,0,0,0.32)); }
`;

const LivePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: rgba(16,185,129,0.10);
  border: 1px solid rgba(16,185,129,0.30);
  font-size: 10px;
  font-weight: 700;
  color: #10b981;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;

  .ld {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 4px #10b981;
    animation: ${orbPulse} 1.6s ease-out infinite;
  }
`;

const CoinNameRow = styled(motion.div)`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${N};
  text-align: center;
  letter-spacing: -0.01em;
`;

const PriceLg = styled(motion.div)`
  font-size: 2.35rem;
  font-weight: 800;
  color: ${N};
  letter-spacing: -0.035em;
  text-align: center;
  line-height: 1;
`;

const ChangeChip = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.88rem;
  font-weight: 700;
  background: ${p => p.$up ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.10)'};
  color: ${p => p.$up ? '#10b981' : '#ef4444'};
  border: 1px solid ${p => p.$up ? 'rgba(16,185,129,0.28)' : 'rgba(239,68,68,0.22)'};
  transition: background 0.4s ease, color 0.4s ease;
`;

const DotRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
`;

const Dot = styled.div`
  height: 5px;
  width: ${p => p.$a ? 24 : 5}px;
  border-radius: 9999px;
  background: ${p => p.$a ? E : 'rgba(13,34,53,0.18)'};
  transition: width 0.38s ease, background 0.38s ease;
`;

const ORBIT_DOTS = [
  { sz:5, dur:8,   dl:0,   cw:true,  op:0.80 },
  { sz:4, dur:13,  dl:2.2, cw:false, op:0.60 },
  { sz:6, dur:6.5, dl:1,   cw:true,  op:0.90 },
  { sz:3, dur:10,  dl:3.5, cw:false, op:0.55 },
  { sz:5, dur:16,  dl:0.5, cw:true,  op:0.45 },
];

function CoinCarousel() {
  const [idx, setIdx]   = useState(0);
  const prices = useLivePrices();

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % COINS.length), 3000);
    return () => clearInterval(t);
  }, []);

  const coin    = COINS[idx];
  const live    = prices[coin.sym] || DEFAULT_PRICES[coin.sym];
  const price   = live?.price;
  const change  = live?.change ?? 0;
  const isUp    = change >= 0;
  const chgStr  = (change >= 0 ? '+' : '') + change.toFixed(2) + '%';
  const CoinLogo = coin.icon;

  const coinVar = {
    enter:  { opacity:0, scale:0.35, rotateY:-85, filter:'blur(8px)' },
    center: { opacity:1, scale:1,    rotateY:0,   filter:'blur(0px)' },
    exit:   { opacity:0, scale:1.25, rotateY:85,  filter:'blur(6px)' },
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>

      {/* Stage: orbit particles + glow + big coin */}
      <CarouselStage>
        <GlowBurst $c={glowAlpha(coin.glow, 0.18)} />

        {/* Orbit dots — colored to match coin */}
        <OrbitRingEl>
          {ORBIT_DOTS.map((o, i) => (
            <OrbitDot key={i} $sz={o.sz} $dur={o.dur} $dl={o.dl} $cw={o.cw} $op={o.op} $c={coin.color} />
          ))}
        </OrbitRingEl>

        {/* Coin */}
        <AnimatePresence mode="popLayout">
          <BigCoinEl
            key={coin.sym}
            $bg={`linear-gradient(145deg,${coin.color} 0%,${coin.dark} 100%)`}
            $glow={coin.glow}
            $glowSoft={glowAlpha(coin.glow, 0.28)}
            variants={coinVar}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type:'spring', stiffness:240, damping:20 }}
            style={{ transformPerspective:550 }}
          >
            <CoinLogo className="ci" style={{ fontSize:coin.iconSize }} aria-hidden="true" />
          </BigCoinEl>
        </AnimatePresence>
      </CarouselStage>

      {/* Live indicator */}
      <LivePill>
        <span className="ld" />
        Live Price
      </LivePill>

      {/* Coin name + price + change — animated on coin switch */}
      <AnimatePresence mode="wait">
        <CoinNameRow
          key={coin.sym + '-n'}
          initial={{ opacity:0, y:10 }}
          animate={{ opacity:1, y:0 }}
          exit={{ opacity:0, y:-8 }}
          transition={{ duration:0.25 }}
        >
          {coin.name} · {coin.sym}
        </CoinNameRow>
      </AnimatePresence>

      {/* Price — re-keys on every price update for flash */}
      <AnimatePresence mode="wait">
        <PriceLg
          key={String(price?.toFixed(2))}
          initial={{ opacity:0.4, y:6 }}
          animate={{ opacity:1, y:0 }}
          exit={{ opacity:0, y:-4 }}
          transition={{ duration:0.18 }}
        >
          {fmtPrice(price)}
        </PriceLg>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <ChangeChip
          key={chgStr}
          $up={isUp}
          initial={{ opacity:0, scale:0.88 }}
          animate={{ opacity:1, scale:1 }}
          exit={{ opacity:0, scale:0.88 }}
          transition={{ duration:0.2 }}
        >
          {isUp ? '▲' : '▼'} {chgStr}
        </ChangeChip>
      </AnimatePresence>

      {/* Progress dots */}
      <DotRow>
        {COINS.map((_, i) => <Dot key={i} $a={i === idx} />)}
      </DotRow>
    </div>
  );
}

/* ─── Premium Dashboard Card (wraps carousel) ─── */

const DashCardWrap = styled(motion.div)`
  background: rgba(255,255,255,0.86);
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  border: 1px solid rgba(255,255,255,0.82);
  border-radius: 28px;
  padding: 18px 20px 20px;
  width: 330px;
  position: relative;
  box-shadow:
    0 0 0 1px rgba(13,34,53,0.07) inset,
    0 28px 80px rgba(13,34,53,0.13),
    0 8px 24px rgba(13,34,53,0.07);

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 18px; right: 18px;
    height: 2px;
    background: linear-gradient(90deg, ${E} 0%, #9945FF 50%, ${OR} 100%);
    border-radius: 0 0 2px 2px;
  }
`;

const DChRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .dct {
    font-size: 10.5px; font-weight: 700; color: ${MT};
    text-transform: uppercase; letter-spacing: 0.08em;
  }
`;

const DCLivePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: rgba(16,185,129,0.10);
  border: 1px solid rgba(16,185,129,0.28);
  font-size: 9.5px; font-weight: 800; color: #10b981;
  letter-spacing: 0.08em; text-transform: uppercase;

  .ld {
    width: 5px; height: 5px; border-radius: 50%;
    background: #10b981; box-shadow: 0 0 6px #10b981;
    animation: ${orbPulse} 1.6s ease-out infinite;
  }
`;

const DCDiv = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, ${BR} 30%, ${BR} 70%, transparent);
  margin: 10px 0;
`;

const DCSigSection = styled.div`
  .srow { display:flex; justify-content:space-between; align-items:center; font-size:11px; color:${MT}; margin-bottom:5px; }
  .spct { font-weight:800; color:${N}; font-size:12px; }
  .sbar { height:6px; border-radius:9999px; background:${FG}; border:1px solid ${BR}; overflow:hidden; margin-bottom:10px; }
  .sfill { height:100%; border-radius:9999px; background:linear-gradient(90deg,${E} 0%,#1568a8 100%); }
`;

const DCStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;

  .si {
    background: ${FG};
    border: 1px solid ${BR};
    border-radius: 10px;
    padding: 8px 6px;
    text-align: center;
  }
  .sl { font-size: 9px; color: ${MT}; margin-bottom: 2px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .sv { font-size: 12.5px; font-weight: 800; color: ${N}; }
  .sc { font-size: 10px; font-weight: 700; }
`;

function PremiumDashCard() {
  return (
    <DashCardWrap
      initial={{ opacity:0, y:26, scale:0.95 }}
      animate={{ opacity:1, y:0, scale:1 }}
      transition={{ duration:0.75, delay:0.3, ease:'easeOut' }}
    >
      <DChRow>
        <span className="dct">AI Market Analysis</span>
        <DCLivePill><span className="ld" />Live</DCLivePill>
      </DChRow>

      <CoinCarousel />

      <DCDiv />

      <DCSigSection>
        <div className="srow">
          <span>AI Signal Strength</span>
          <span className="spct">87%</span>
        </div>
        <div className="sbar"><div className="sfill" style={{ width:'87%' }} /></div>
      </DCSigSection>

      <DCStatsGrid>
        <div className="si">
          <div className="sl">24h Vol</div>
          <div className="sv">$28.4B</div>
        </div>
        <div className="si">
          <div className="sl">Mkt Cap</div>
          <div className="sv">$1.28T</div>
        </div>
        <div className="si">
          <div className="sl">Dominance</div>
          <div className="sv sc" style={{ color:'#10b981' }}>BTC 52%</div>
        </div>
      </DCStatsGrid>
    </DashCardWrap>
  );
}

/* ─── Hero Big Coin (dark bg, full-size display) ─── */

const HeroCoinEl = styled(motion.div)`
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 26%, rgba(255,255,255,0.60) 0%, rgba(255,255,255,0.26) 12%, transparent 28%),
    ${p => p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255,255,255,0.38);
  box-shadow:
    0 0 70px  ${p => p.$glow},
    0 0 130px ${p => p.$glowSoft},
    0 30px 70px rgba(13,34,53,0.18),
    inset 0 5px 0 rgba(255,255,255,0.54),
    inset 0 -8px 0 rgba(0,0,0,0.22),
    inset 3px 0 rgba(255,255,255,0.12),
    inset -3px 0 rgba(0,0,0,0.10);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 14px;
    border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.18);
    z-index: 1;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(155deg, rgba(255,255,255,0.18) 0%, transparent 52%);
    z-index: 1;
    pointer-events: none;
  }

  .ci {
    position: relative;
    z-index: 2;
    color: rgba(255,255,255,0.97);
    filter: drop-shadow(0 6px 14px rgba(0,0,0,0.42));
  }
`;

const HeroCoinName = styled(motion.div)`
  font-size: 1rem;
  font-weight: 600;
  color: ${MT};
  text-align: center;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const HeroPriceLg = styled(motion.div)`
  font-size: 3rem;
  font-weight: 800;
  color: ${N};
  letter-spacing: -0.04em;
  text-align: center;
  line-height: 1;
`;

const HeroChangeChip = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-top: 10px;
  background: ${p => p.$up ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.09)'};
  color: ${p => p.$up ? '#059669' : '#dc2626'};
  border: 1px solid ${p => p.$up ? 'rgba(16,185,129,0.26)' : 'rgba(239,68,68,0.22)'};
`;

const HeroLivePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 9999px;
  background: rgba(16,185,129,0.08);
  border: 1px solid rgba(16,185,129,0.24);
  font-size: 10px;
  font-weight: 700;
  color: #059669;
  letter-spacing: 0.09em;
  text-transform: uppercase;

  .ld {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 6px #10b981;
    animation: ${orbPulse} 1.6s ease-out infinite;
  }
`;

const HeroDotRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 4px;
`;

const HeroDot = styled.div`
  height: 4px;
  width: ${p => p.$a ? 22 : 4}px;
  border-radius: 9999px;
  background: ${p => p.$a ? E : 'rgba(13,34,53,0.18)'};
  transition: width 0.38s ease, background 0.38s ease;
`;

function HeroBigCoinDisplay() {
  const [idx, setIdx] = useState(0);
  const prices = useLivePrices();

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % COINS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const coin   = COINS[idx];
  const live   = prices[coin.sym] || DEFAULT_PRICES[coin.sym];
  const price  = live?.price;
  const change = live?.change ?? 0;
  const isUp   = change >= 0;
  const chgStr = (change >= 0 ? '+' : '') + change.toFixed(2) + '%';
  const CoinLogo = coin.icon;

  const coinVar = {
    enter:  { opacity:0, scale:0.55, rotateY:-80, filter:'blur(10px)' },
    center: { opacity:1, scale:1,    rotateY:0,   filter:'blur(0px)'  },
    exit:   { opacity:0, scale:1.15, rotateY:80,  filter:'blur(8px)'  },
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:24 }}>
      {/* Coin Stage */}
      <div style={{ position:'relative', width:360, height:360, display:'flex', alignItems:'center', justifyContent:'center' }}>

        {/* Radial glow behind coin */}
        <div style={{
          position:'absolute',
          top:'50%', left:'50%',
          width:520, height:520,
          transform:'translate(-50%,-50%)',
          borderRadius:'50%',
          background:`radial-gradient(circle, ${glowAlpha(coin.glow, 0.18)} 0%, transparent 60%)`,
          transition:'background 1s ease',
          pointerEvents:'none',
          zIndex:0,
        }} />

        {/* Outer subtle ring */}
        <div style={{
          position:'absolute',
          top:'50%', left:'50%',
          width:380, height:380,
          transform:'translate(-50%,-50%)',
          borderRadius:'50%',
          border:'1px solid rgba(255,255,255,0.06)',
          pointerEvents:'none',
          zIndex:1,
        }} />

        {/* Float + flip wrapper */}
        <motion.div
          animate={{ y:[0,-22,0] }}
          transition={{ duration:5, repeat:Infinity, ease:'easeInOut' }}
          style={{ position:'relative', zIndex:2 }}
        >
          <AnimatePresence mode="popLayout">
            <HeroCoinEl
              key={coin.sym}
              $bg={`linear-gradient(145deg, ${coin.color} 0%, ${coin.dark} 100%)`}
              $glow={coin.glow}
              $glowSoft={glowAlpha(coin.glow, 0.20)}
              variants={coinVar}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type:'spring', stiffness:220, damping:20 }}
              style={{ transformPerspective:700 }}
            >
              <img
                src={COIN_IMGS[coin.sym]}
                alt={coin.name}
                style={{ width:'60%', height:'60%', objectFit:'contain', position:'relative', zIndex:2, filter:'drop-shadow(0 6px 16px rgba(0,0,0,0.45)) brightness(1.05)' }}
                onError={e => { e.currentTarget.style.display='none'; }}
              />
            </HeroCoinEl>
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Live indicator */}
      <HeroLivePill><span className="ld" />Live Price</HeroLivePill>

      {/* Name + price + change */}
      <div style={{ textAlign:'center' }}>
        <AnimatePresence mode="wait">
          <HeroCoinName
            key={coin.sym + '-n'}
            initial={{ opacity:0, y:8 }}
            animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:-6 }}
            transition={{ duration:0.22 }}
          >
            {coin.name} · {coin.sym}
          </HeroCoinName>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <HeroPriceLg
            key={String(price?.toFixed(2))}
            initial={{ opacity:0.4, y:7 }}
            animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:-5 }}
            transition={{ duration:0.18 }}
          >
            {fmtPrice(price)}
          </HeroPriceLg>
        </AnimatePresence>

        <div style={{ display:'flex', justifyContent:'center' }}>
          <AnimatePresence mode="wait">
            <HeroChangeChip
              key={chgStr}
              $up={isUp}
              initial={{ opacity:0, scale:0.88 }}
              animate={{ opacity:1, scale:1 }}
              exit={{ opacity:0, scale:0.88 }}
              transition={{ duration:0.2 }}
            >
              {isUp ? '▲' : '▼'} {chgStr}
            </HeroChangeChip>
          </AnimatePresence>
        </div>
      </div>

      {/* Progress dots */}
      <HeroDotRow>
        {COINS.map((_, i) => <HeroDot key={i} $a={i === idx} />)}
      </HeroDotRow>
    </div>
  );
}

/* Floating metric cards */
const FloatMetric = styled(motion.div)`
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(13,34,53,0.09);
  border-radius: 14px;
  padding: 10px 16px;
  box-shadow: 0 6px 24px rgba(13,34,53,0.10), 0 2px 6px rgba(13,34,53,0.05);
  display: flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  color: ${N};

  .md {
    width: 9px; height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

/* VaultShield-style pill CTA */
const VaultCTA = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  background: ${E};
  color: #ffffff;
  border-radius: 50px;
  padding: 16px 22px 16px 28px;
  font-weight: 600;
  font-size: clamp(0.9rem, 2vw, 1rem);
  min-width: 210px;
  box-shadow: 0 4px 28px rgba(41,171,226,0.38);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border: none;

  .arr {
    width: 40px; height: 40px;
    background: rgba(255,255,255,0.18);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.1rem;
  }
`;

const SecondaryHeroPill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: ${PA};
  color: ${N};
  border: 1.5px solid rgba(13,34,53,0.18);
  border-radius: 50px;
  padding: 16px 26px;
  font-weight: 600;
  font-size: clamp(0.9rem, 2vw, 1rem);
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(13,34,53,0.08);
  transition: background 0.18s ease, box-shadow 0.18s ease;

  &:hover { background: ${FG}; box-shadow: 0 4px 16px rgba(13,34,53,0.12); }
`;

const PrimaryHeroPill = styled(SecondaryHeroPill)`
  background: ${E};
  color: #ffffff;
  border-color: ${E};
  box-shadow: 0 4px 20px rgba(41,171,226,0.30);

  &:hover {
    background: ${Eo};
    border-color: ${Eo};
    box-shadow: 0 6px 28px rgba(41,171,226,0.40);
  }
`;

/* Hero trust stats row */
const HeroStatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-wrap: wrap;
  margin-top: 1.1rem;
  @media (max-width: 960px) { justify-content: center; }

  .hs  { display:flex; align-items:center; gap:0.38rem; font-size:12.5px; color:${MT}; }
  .hsn { font-weight:700; color:${N}; }
  .hss { width:1px; height:14px; background:${BR}; }
`;

/* AI Orb */
const OrbCore = styled.div`
  width: 54px; height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${E} 0%, #1568a8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.35rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 25px rgba(41,171,226,0.5), 0 4px 14px rgba(13,34,53,0.22);
`;

const PulseRing = styled.div`
  position: absolute;
  width: 54px; height: 54px;
  border-radius: 50%;
  border: 1.5px solid rgba(41,171,226,${p => p.$op || 0.4});
  animation: ${orbPulse} 2.4s ease-out ${p => p.$delay || 0}s infinite;
`;

function BgChart() {
  return (
    <svg viewBox="0 0 480 90" fill="none" aria-hidden="true" style={{
      position:'absolute', bottom:'12%', left:'2%',
      width:'96%', opacity:0.055, zIndex:1, pointerEvents:'none',
    }}>
      <path d="M0 78 C38 72,58 82,95 62 C132 42,152 57,195 38 C238 19,258 34,300 17 C342 0,362 10,405 5 L455 2 L480 0"
        stroke="#29ABE2" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      {[[18,71,13,true],[50,62,11,false],[82,54,12,true],[114,48,10,true],
        [146,41,12,false],[178,34,11,true],[210,28,10,true],
        [242,22,11,true],[274,16,10,true],[306,10,9,true]]
        .map(([x,y,h,bull],i) => (
          <g key={i}>
            <line x1={x+4} y1={y-3} x2={x+4} y2={y+h+3} stroke={bull?'#10b981':'#ef4444'} strokeWidth="0.9"/>
            <rect x={x} y={y} width="8" height={h} fill={bull?'#10b981':'#ef4444'} rx="1"/>
          </g>
        ))}
    </svg>
  );
}

/* ─── Parallax hook ─── */
function useParallax(containerRef) {
  const mX = useMotionValue(0);
  const mY = useMotionValue(0);
  const sX = useSpring(mX, { stiffness: 42, damping: 20 });
  const sY = useSpring(mY, { stiffness: 42, damping: 20 });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    if (window.innerWidth < 960) return;

    const handleMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      mX.set((e.clientX - rect.left - rect.width / 2) / rect.width);
      mY.set((e.clientY - rect.top - rect.height / 2) / rect.height);
    };
    const handleLeave = () => { mX.set(0); mY.set(0); };

    const el = containerRef.current;
    el?.addEventListener('mousemove', handleMove);
    el?.addEventListener('mouseleave', handleLeave);
    return () => {
      el?.removeEventListener('mousemove', handleMove);
      el?.removeEventListener('mouseleave', handleLeave);
    };
  }, [containerRef, mX, mY]);

  return { sX, sY };
}

const BG_FLOAT_COINS = [
  { sym:'BTC', size:130, top:'4%',  right:'6%',  opacity:0.18, dur:8.5, delay:0,   dy:-20 },
  { sym:'ETH', size:88,  bottom:'8%', right:'4%', opacity:0.14, dur:11,  delay:1.4, dy:18  },
  { sym:'SOL', size:72,  top:'52%', right:'2%',  opacity:0.12, dur:9,   delay:2.2, dy:-14 },
  { sym:'BNB', size:64,  top:'8%',  left:'4%',   opacity:0.10, dur:7.5, delay:0.8, dy:-12 },
  { sym:'ADA', size:56,  bottom:'18%', left:'3%', opacity:0.09, dur:10,  delay:2.8, dy:16  },
  { sym:'XRP', size:50,  top:'35%', left:'2%',   opacity:0.08, dur:8.2, delay:1.6, dy:-10 },
];

function CryptoVisual() {
  const wrapRef = useRef(null);
  const { sX, sY } = useParallax(wrapRef);

  const nearX = useTransform(sX, [-0.5, 0.5], [-6, 6]);
  const nearY = useTransform(sY, [-0.5, 0.5], [-4, 4]);

  return (
    <VisualWrap ref={wrapRef} aria-hidden="true">

      {/* ── BACKGROUND: floating coin images ── */}
      {BG_FLOAT_COINS.map((c, i) => (
        <motion.img
          key={c.sym}
          src={COIN_IMGS[c.sym]}
          alt=""
          aria-hidden="true"
          initial={{ opacity:0, scale:0.7 }}
          animate={{ opacity:c.opacity, scale:1, y:[0, c.dy, 0] }}
          transition={{
            opacity:{ duration:1, delay:c.delay + 0.3 },
            scale:{ duration:1, delay:c.delay + 0.3 },
            y:{ duration:c.dur, repeat:Infinity, ease:'easeInOut', delay:c.delay },
          }}
          style={{
            position:'absolute',
            width:c.size, height:c.size,
            objectFit:'contain',
            pointerEvents:'none',
            zIndex:1,
            filter:'blur(0.5px) brightness(0.9)',
            ...( c.top    ? { top:    c.top    } : {} ),
            ...( c.bottom ? { bottom: c.bottom } : {} ),
            ...( c.right  ? { right:  c.right  } : {} ),
            ...( c.left   ? { left:   c.left   } : {} ),
          }}
          onError={e => { e.currentTarget.style.display='none'; }}
        />
      ))}

      {/* ── MAIN: Big coin centered ── */}
      <motion.div
        initial={{ opacity:0, scale:0.88, y:24 }}
        animate={{ opacity:1, scale:1, y:0 }}
        transition={{ duration:0.9, delay:0.2, ease:[0.22,1,0.36,1] }}
        style={{ position:'relative', zIndex:10 }}
      >
        <HeroBigCoinDisplay />
      </motion.div>

      {/* ── NEAR LAYER: floating metric chips ── */}
      <motion.div style={{ position:'absolute', inset:0, x:nearX, y:nearY, zIndex:24, pointerEvents:'none' }}>

        {/* Top-right chip */}
        <FloatMetric
          style={{ position:'absolute', top:'6%', right:'2%' }}
          initial={{ opacity:0, x:20 }}
          animate={{ opacity:1, x:0, y:[0,-10,0] }}
          transition={{
            opacity:{ duration:0.5, delay:0.9 },
            x:{ duration:0.5, delay:0.9 },
            y:{ duration:5.8, repeat:Infinity, ease:'easeInOut', delay:1.2 },
          }}
        >
          <div className="md" style={{ background:'#4ade80', boxShadow:'0 0 6px #4ade80' }} />
          Chart Scanner
        </FloatMetric>

        {/* Bottom-left chip */}
        <FloatMetric
          style={{ position:'absolute', bottom:'8%', left:'2%' }}
          initial={{ opacity:0, x:-20 }}
          animate={{ opacity:1, x:0, y:[0,10,0] }}
          transition={{
            opacity:{ duration:0.5, delay:1.1 },
            x:{ duration:0.5, delay:1.1 },
            y:{ duration:6.8, repeat:Infinity, ease:'easeInOut', delay:1.8 },
          }}
        >
          <div className="md" style={{ background:E, boxShadow:`0 0 6px ${E}` }} />
          AI Signals
        </FloatMetric>

        {/* Top-left chip */}
        <FloatMetric
          style={{ position:'absolute', top:'6%', left:'2%' }}
          initial={{ opacity:0, x:-20 }}
          animate={{ opacity:1, x:0, y:[0,-8,0] }}
          transition={{
            opacity:{ duration:0.5, delay:1.3 },
            x:{ duration:0.5, delay:1.3 },
            y:{ duration:7.4, repeat:Infinity, ease:'easeInOut', delay:2.4 },
          }}
        >
          <FaBrain style={{ color:E, fontSize:13, flexShrink:0 }} />
          Market Insights
        </FloatMetric>

      </motion.div>

    </VisualWrap>
  );
}

function HomeHero() {
  return (
    <HeroWrap>
      <HeroInner>
        <HeroLeft>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fUp}
            custom={0}
          >
            <Badge>
              <FiZap style={{ color:E, fontSize:'0.85rem' }} aria-hidden="true" />
              AI-Powered · Real-Time Signals
            </Badge>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fUp}
            custom={1}
          >
            <DisplayH1>
              <FiZap
                className="hi"
                style={{ color:OR, fontSize:'clamp(1.4rem,4vw,2.6rem)', marginRight:'10px' }}
                aria-hidden="true"
              />
              Catch Every<br />
              Crypto Signal{' '}
              <FiTrendingUp
                className="hi"
                style={{ color:E, fontSize:'clamp(1.3rem,3.8vw,2.4rem)' }}
                aria-hidden="true"
              /><br />
              In <em>Real-Time.</em>
            </DisplayH1>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fUp}
            custom={2}
          >
            <HeroSub>
              Zero guesswork, total clarity. Trend Genie's AI scans Bitcoin, Ethereum,
              and top altcoins 24/7 — delivering live signals, trend alerts, and
              market insights right to your phone. Free for Android &amp; iOS.
            </HeroSub>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fUp}
            custom={3}
          >
            <HeroBtnRow style={{ alignItems:'center' }}>
              <PrimaryHeroPill
                href={APP_LINKS.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGooglePlay aria-hidden="true" />
                Google Play
              </PrimaryHeroPill>
              <SecondaryHeroPill
                href={APP_LINKS.appStore}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaApple aria-hidden="true" />
                App Store
              </SecondaryHeroPill>
            </HeroBtnRow>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fUp}
            custom={4}
          >
            <HeroStatsRow>
              <div className="hs">
                <span style={{ color:'#f59e0b' }}>★★★★★</span>
                <span className="hsn">4.8</span> Rating
              </div>
              <span className="hss" />
              <div className="hs">
                <FaUsers style={{ color:E, fontSize:'0.85rem' }} />
                <span className="hsn">50K+</span> Downloads
              </div>
              <span className="hss" />
              <div className="hs">
                <FaCheckCircle style={{ color:'#10b981', fontSize:'0.85rem' }} />
                <span className="hsn">7 Days</span> Free Trail 
              </div>
            </HeroStatsRow>
          </motion.div>

        </HeroLeft>

        <motion.div
          initial={{ opacity:0, x:40 }}
          animate={{ opacity:1, x:0 }}
          transition={{ duration:0.8, delay:0.25, ease:[0.22,1,0.36,1] }}
        >
          <CryptoVisual />
        </motion.div>
      </HeroInner>
    </HeroWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ②  PROBLEM SECTION
═══════════════════════════════════════════════════════ */

const ProblemWrap = styled.section`
  background: ${FG} !important;
`;

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 1rem;
  max-width: 1120px;
  margin: 2.4rem auto 0;
`;

const ProblemCard = styled(motion.div)`
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  padding: 22px 20px;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .emo { font-size: 1.7rem; margin-bottom: 0.75rem; display:block; }
  h3 { font-size: 0.95rem; font-weight: 700; color:${N}; margin:0 0 0.4rem; line-height:1.3; }
  p  { font-size: 0.84rem; color:${MT}; line-height:1.62; margin:0; }
`;

const PROBLEMS = [
  { e:'🔊', t:'Too Much Market Noise',    d:'Endless alerts, opinions, and conflicting signals make it hard to know what truly matters.' },
  { e:'📉', t:'Confusing Charts',          d:'Technical analysis can feel overwhelming without the right tools to interpret chart patterns.' },
  { e:'😰', t:'Emotional Trading',         d:'Fear and greed lead to impulsive decisions that undermine even the best trading strategies.' },
  { e:'👀', t:'Missing Key Trends',        d:'Fast-moving markets mean important opportunities can slip by before you have time to react.' },
  { e:'📊', t:'Tracking Multiple Coins',   d:'Scanning dozens of cryptocurrencies simultaneously is exhausting and error-prone.' },
];

function ProblemSection() {
  return (
    <ProblemWrap>
      <div style={{ maxWidth:1120, margin:'0 auto' }}>
        <Badge>The Challenge</Badge>
        <SectionH2>Crypto trading is harder<br /><em>than it should be.</em></SectionH2>
        <SubCopy style={{ maxWidth:520 }}>
          Millions of crypto users face the same challenges every day.
          Trend Genie was built specifically to solve them.
        </SubCopy>
      </div>
      <ProblemGrid>
        {PROBLEMS.map((p,i) => (
          <ProblemCard key={p.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
            <span className="emo" aria-hidden="true">{p.e}</span>
            <h3>{p.t}</h3>
            <p>{p.d}</p>
          </ProblemCard>
        ))}
      </ProblemGrid>
    </ProblemWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ③  SOLUTION SECTION
═══════════════════════════════════════════════════════ */

const SolutionWrap = styled.section`
  background: ${PA} !important;
`;

const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  max-width: 1100px;
  margin: 2.6rem auto 0;
`;

const FeatureRowItem = styled(motion.div)`
  .ic {
    width: 44px; height: 44px;
    border-radius: 10px;
    background: ${FG};
    border: 1px solid ${BR};
    display: flex; align-items: center; justify-content: center;
    font-size: 1.2rem;
    color: ${E};
    margin-bottom: 1.1rem;
  }
  h3 { font-size: 1.05rem; font-weight: 700; color:${N}; margin:0 0 0.5rem; }
  p  { font-size: 0.9rem;  color:${MT}; line-height:1.7; margin:0; }
`;

const SOLUTIONS = [
  { ic:<FaBrain />,     t:'AI-Powered Market Insights',  d:"Our AI analyzes crypto market conditions in real time and surfaces what matters most — so you can focus on decisions, not data collection." },
  { ic:<FiTrendingUp />,t:'Clear Trend Analysis',        d:"Understand where the crypto market is heading with easy-to-read trend analysis. See Bitcoin and altcoin trends at a glance." },
  { ic:<FiTarget />,    t:'Smarter Decision Support',    d:"Crypto signal insights and chart-based analysis help you think more clearly and reduce costly emotional reactions." },
];

function SolutionSection() {
  return (
    <SolutionWrap>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Badge>The Solution</Badge>
        <SectionH2>Trend Genie<br /><em>simplifies everything.</em></SectionH2>
        <SubCopy style={{ maxWidth:520 }}>
          One AI-powered app bringing together crypto market analysis, trend
          detection, and signal insights so you trade with more clarity.
        </SubCopy>
      </div>
      <FeatureRow>
        {SOLUTIONS.map((s,i) => (
          <FeatureRowItem key={s.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
            <div className="ic">{s.ic}</div>
            <h3>{s.t}</h3>
            <p>{s.d}</p>
          </FeatureRowItem>
        ))}
      </FeatureRow>
    </SolutionWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ④  FEATURES SECTION
═══════════════════════════════════════════════════════ */

const FeatWrap = styled.section`
  background: ${FG} !important;
`;

const FeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  max-width: 1200px;
  margin: 2.6rem auto 0;
`;

const FeatCard = styled(motion.div)`
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  padding: 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .fi {
    width: 40px; height: 40px;
    border-radius: 9999px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem;
    border: 1px solid ${BR};
  }
  .ftag {
    display: inline-flex;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    background: ${FG};
    color: ${Eo};
    border: 1px solid ${BR};
    width: fit-content;
  }
  h3 { font-size: 0.93rem; font-weight: 700; color:${N}; margin:0; line-height:1.35; }
  p  { font-size: 0.83rem; color:${MT}; line-height:1.65; margin:0; flex:1; }
`;

const FEATURES = [
  { ic:<FaBrain />,      tag:'AI Core',       c:'#1a8fc0', bg:'#daeeff', t:'AI Market Analysis',         d:'Advanced AI scans crypto conditions and delivers actionable market analysis to your device.' },
  { ic:<FiTrendingUp />, tag:'Trends',         c:'#10b981', bg:'#d1fae5', t:'Crypto Trend Detection',      d:'Identify emerging trends in Bitcoin, Ethereum, and altcoins before the broader market catches on.' },
  { ic:<FaBell />,       tag:'Signals',        c:'#d97706', bg:'#fef3c7', t:'Trading Signal Insights',     d:'AI-generated signal insights with clear context around current market conditions.' },
  { ic:<FaBitcoin />,    tag:'Multi-Coin',     c:'#ea580c', bg:'#ffedd5', t:'Bitcoin & Altcoin Analysis',  d:'From BTC to the latest altcoins — Trend Genie covers the coins that matter most.' },
  { ic:<FiBarChart2 />,  tag:'Technical',      c:'#7c3aed', bg:'#ede9fe', t:'Technical Analysis Support',  d:'Understand chart patterns and market structure with AI-assisted crypto chart analysis.' },
  { ic:<FiGlobe />,      tag:'Real-Time',      c:'#0369a1', bg:'#e0f2fe', t:'Real-Time Market Overview',   d:'Stay updated with a live overview of crypto market conditions and significant moves.' },
  { ic:<FaLightbulb />,  tag:'Beginner',       c:'#16a34a', bg:'#dcfce7', t:'Beginner-Friendly Interface', d:'Clean, simple design makes crypto market analysis accessible without a steep learning curve.' },
  { ic:<FaMobileAlt />,  tag:'Cross-Platform', c:'#0d2235', bg:'#f0f9ff', t:'Android & iOS Access',        d:'Full access to AI crypto market analysis on Google Play and the Apple App Store.' },
];

function FeaturesSection() {
  return (
    <FeatWrap>
      <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <Badge>Features</Badge>
        <SectionH2>Everything you need for<br /><em>crypto market analysis.</em></SectionH2>
        <SubCopy style={{ maxWidth:520 }}>
          Powerful AI-driven features built for crypto traders of every experience level.
        </SubCopy>
      </div>
      <FeatGrid>
        {FEATURES.map((f,i) => (
          <FeatCard key={f.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.1 }} variants={fUp}>
            <div className="fi" style={{ background:f.bg, color:f.c, borderColor:`${f.c}33` }}>{f.ic}</div>
            <span className="ftag">{f.tag}</span>
            <h3>{f.t}</h3>
            <p>{f.d}</p>
          </FeatCard>
        ))}
      </FeatGrid>
    </FeatWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑤  HOW IT WORKS
═══════════════════════════════════════════════════════ */

const HowWrap = styled.section`
  background: ${PA} !important;
`;

const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2.4rem;
  max-width: 1100px;
  margin: 2.6rem auto 0;
  position: relative;

  @media (min-width: 900px) {
    &::before {
      content: '';
      position: absolute;
      top: 20px;
      left: 10%; right: 10%;
      height: 1px;
      background: repeating-linear-gradient(
        90deg, ${BM} 0, ${BM} 5px, transparent 5px, transparent 14px
      );
      z-index: 0;
    }
  }
`;

const StepItem = styled(motion.div)`
  position: relative;
  z-index: 1;
  text-align: center;

  .sn {
    width: 40px; height: 40px;
    border-radius: 9999px;
    background: ${N};
    color: ${PA};
    font-size: 0.95rem;
    font-weight: 800;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 1.1rem;
    border: 2px solid ${PA};
    box-shadow: 0 0 0 1.5px ${N};
  }
  h3 { font-size: 1rem; font-weight: 700; color:${N}; margin:0 0 0.45rem; }
  p  { font-size: 0.86rem; color:${MT}; line-height:1.65; margin:0 auto; max-width:210px; }
`;

const STEPS = [
  { n:1, t:'Download the App',         d:'Get Trend Genie free on Google Play or the Apple App Store. Setup takes under a minute.' },
  { n:2, t:'Explore Market Insights',  d:'Browse AI-generated crypto market analysis and trend overviews from your home screen.' },
  { n:3, t:'Review Signals & Trends',  d:'Check crypto signal insights and trend analysis to understand what the market is doing.' },
  { n:4, t:'Make Informed Decisions',  d:'Use the AI analysis to support your own research and make more confident trading choices.' },
];

function HowItWorksSection() {
  return (
    <HowWrap>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <Badge>How It Works</Badge>
        <SectionH2>Get started in<br /><em>4 simple steps.</em></SectionH2>
        <SubCopy style={{ maxWidth:480 }}>
          No technical expertise required — Trend Genie gets you up and running quickly.
        </SubCopy>
      </div>
      <StepsRow>
        {STEPS.map((s,i) => (
          <StepItem key={s.n} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
            <div className="sn">{s.n}</div>
            <h3>{s.t}</h3>
            <p>{s.d}</p>
          </StepItem>
        ))}
      </StepsRow>
    </HowWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑥  DOWNLOAD CTA — dark navy inverted
═══════════════════════════════════════════════════════ */

const DlWrap = styled.section`
  background: ${N} !important;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content:'';
    position:absolute;
    top:-40%; right:-10%;
    width:600px; height:600px;
    background: radial-gradient(circle, rgba(41,171,226,0.12) 0%, transparent 70%);
    border-radius:50%;
    pointer-events:none;
  }
`;

const DlH2 = styled(DisplayH2)`
  color: ${PA} !important;
  -webkit-text-fill-color: ${PA} !important;
  em { color: ${E} !important; -webkit-text-fill-color: ${E} !important; }
`;

const DlSub = styled.p`
  font-size: 1.04rem;
  color: rgba(255,255,255,0.7) !important;
  line-height: 1.72;
  max-width: 490px;
  margin: 0 auto 2.4rem;
`;

const DlBtnRow = styled.div`
  display: flex;
  gap: 0.85rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.3rem;
`;

const DlNote = styled.p`
  color: rgba(255,255,255,0.38) !important;
  font-size: 12.5px !important;
  margin: 0 !important;
`;

function DownloadCTASection() {
  return (
    <DlWrap>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.3 }} variants={fUp}>
        <DarkBadge>Download Free</DarkBadge>
        <DlH2>
          Start your<br />
          <em>AI crypto analysis</em><br />
          journey today.
        </DlH2>
        <DlSub>
          Join thousands of crypto users who use Trend Genie to cut through the
          market noise and understand crypto trends better. Free on Android and iOS.
        </DlSub>
        <DlBtnRow>
          <WhitePill href={APP_LINKS.googlePlay} target="_blank" rel="noopener noreferrer" aria-label="Get Trend Genie on Google Play">
            <FaGooglePlay aria-hidden="true" />
            <span className="bt">
              <span className="bs">Get it on</span>
              <span className="bm">Google Play</span>
            </span>
          </WhitePill>
          <GhostPill href={APP_LINKS.appStore} target="_blank" rel="noopener noreferrer" aria-label="Download Trend Genie on the App Store">
            <FaApple aria-hidden="true" />
            <span className="bt">
              <span className="bs">Download on the</span>
              <span className="bm">App Store</span>
            </span>
          </GhostPill>
        </DlBtnRow>
        <DlNote>Free to download · Available on Android &amp; iOS · No card required</DlNote>
      </motion.div>
    </DlWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑦  WHY CHOOSE TREND GENIE
═══════════════════════════════════════════════════════ */

const WhyWrap = styled.section`
  background: ${FG} !important;
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  max-width: 1120px;
  margin: 2.6rem auto 0;
`;

const WhyCard = styled(motion.div)`
  background: ${PA};
  border: 1px solid ${BR};
  border-radius: 10px;
  padding: 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: ${SL}; border-color: ${BM}; }

  .wi {
    width: 36px; height: 36px;
    border-radius: 9999px;
    background: ${FG};
    border: 1px solid ${BR};
    color: ${E};
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem;
  }
  h3 { font-size: 0.95rem; font-weight: 700; color:${N}; margin:0; }
  p  { font-size: 0.84rem; color:${MT}; line-height:1.65; margin:0; }
`;

const WHY = [
  { ic:<FaUsers />,     t:'Built for Modern Crypto Users', d:"Designed for today's crypto traders — beginner investors or active daily traders." },
  { ic:<FiSmartphone />,t:'Simple, Clean Interface',       d:'No clutter. Just clear AI-powered market analysis you can act on quickly.' },
  { ic:<FaBrain />,     t:'AI-Powered Analysis',           d:'AI-driven crypto insights that would take hours to gather manually.' },
  { ic:<FaLightbulb />, t:'For Beginners & Active Traders',d:"Whether you're new or experienced, Trend Genie adapts to your level." },
  { ic:<FaMobileAlt />, t:'Android & iOS Available',       d:'Full access to Trend Genie AI analysis on both major mobile platforms.' },
  { ic:<FaShieldAlt />, t:'Trustworthy & Transparent',     d:'Analysis and education only — no unrealistic promises, no hype.' },
];

function WhyChooseSection() {
  return (
    <WhyWrap>
      <div style={{ maxWidth:1120, margin:'0 auto' }}>
        <Badge>Why Trend Genie</Badge>
        <SectionH2>Why traders<br /><em>choose Trend Genie.</em></SectionH2>
        <SubCopy style={{ maxWidth:500 }}>
          Trusted by crypto users worldwide for clear, AI-driven market analysis
          that helps them trade with more confidence.
        </SubCopy>
      </div>
      <WhyGrid>
        {WHY.map((r,i) => (
          <WhyCard key={r.t} custom={i} initial="hidden" whileInView="visible" viewport={{ once:true, amount:0.15 }} variants={fUp}>
            <div className="wi">{r.ic}</div>
            <h3>{r.t}</h3>
            <p>{r.d}</p>
          </WhyCard>
        ))}
      </WhyGrid>
    </WhyWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑧  FAQ
═══════════════════════════════════════════════════════ */

const FAQWrap = styled.section`
  background: ${PA} !important;
`;

const FAQList = styled.div`
  max-width: 720px;
  margin: 2.6rem auto 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const FAQItem = styled(motion.div)`
  border: 1px solid ${BR};
  border-radius: 10px;
  overflow: hidden;
  background: ${PA};
  transition: border-color 0.16s ease;
  &:hover { border-color: ${BM}; }
`;

const FAQBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  span { font-size: 0.95rem; font-weight: 600; color: ${N}; line-height: 1.4; }
  .chev { color: ${E}; font-size: 0.78rem; flex-shrink: 0; transition: transform 0.22s ease; }
  &.open .chev { transform: rotate(180deg); }
`;

const FAQAns = styled(motion.div)`
  overflow: hidden;
  padding: 0 20px 16px;
  p { font-size: 0.9rem; color: ${MT}; line-height: 1.72; margin: 0; }
`;

const FAQS = [
  { q:'What is Trend Genie?', a:'Trend Genie is an AI-powered crypto market analysis app available on Android and iOS. It helps users understand cryptocurrency trends, analyze market signals, and review chart-based insights to make more informed trading decisions.' },
  { q:'Is Trend Genie available on iPhone?', a:'Yes. Trend Genie is available on the Apple App Store for iPhone users. You can download it free at apps.apple.com/us/app/trend-genie-ai-market-analysis/id6769978798.' },
  { q:'Is Trend Genie available on Android?', a:'Yes. Trend Genie is available on Google Play for Android devices. Search for "Trend Genie" on Google Play or visit play.google.com to download it free.' },
  { q:'Does Trend Genie provide crypto trading signals?', a:'Trend Genie provides AI-generated crypto signal insights and market trend analysis to help users understand market conditions. These are analytical insights to support your own research, not buy/sell orders.' },
  { q:'Does Trend Genie give financial advice?', a:'No. Trend Genie provides market analysis and educational insights only. It does not constitute financial advice. Always conduct your own research before making any trading or investment decisions.' },
  { q:'Can beginners use Trend Genie?', a:"Absolutely. Trend Genie is designed with a beginner-friendly interface that makes crypto market analysis accessible even if you're new to cryptocurrency." },
  { q:'How does AI crypto analysis work in Trend Genie?', a:"Trend Genie's AI analyzes real-time crypto market data, price action, and trends to surface signal patterns and insights — automating the data analysis process so you can focus on understanding the market." },
];

function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <FAQWrap>
      <div style={{ maxWidth:720, margin:'0 auto' }}>
        <Badge>FAQ</Badge>
        <SectionH2>Frequently asked<br /><em>questions.</em></SectionH2>
        <SubCopy>Everything you need to know about Trend Genie and AI crypto market analysis.</SubCopy>
      </div>
      <FAQList>
        {FAQS.map((f,i) => (
          <FAQItem key={i} initial="hidden" whileInView="visible" viewport={{ once:true }} variants={fUp} custom={i*0.35}>
            <FAQBtn className={open===i?'open':''} onClick={()=>setOpen(open===i?null:i)} aria-expanded={open===i}>
              <span>{f.q}</span>
              <FaChevronDown className="chev" aria-hidden="true" />
            </FAQBtn>
            <AnimatePresence initial={false}>
              {open===i && (
                <FAQAns key="a" initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.22, ease:'easeInOut' }}>
                  <p>{f.a}</p>
                </FAQAns>
              )}
            </AnimatePresence>
          </FAQItem>
        ))}
      </FAQList>
    </FAQWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   ⑨  DISCLAIMER
═══════════════════════════════════════════════════════ */

const DiscWrap = styled.section`
  background: ${PA} !important;
  padding: 48px 10% !important;
  @media (max-width:768px) { padding: 36px 5% !important; }
`;

const DiscBox = styled.div`
  max-width: 840px;
  margin: 0 auto;
  border: 1px solid rgba(247,148,29,0.35);
  border-left: 4px solid ${OR};
  border-radius: 10px;
  padding: 20px 24px;
  background: #fffcf5;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  .di { font-size: 1.1rem; color:${OR}; flex-shrink:0; margin-top:0.1rem; }
  h3 { font-size: 0.9rem !important; font-weight:700; color:${N} !important; margin:0 0 0.38rem; }
  p  { font-size: 0.82rem; color:${MT} !important; line-height:1.7; margin:0; }

  @media (max-width:560px) { flex-direction:column; gap:0.75rem; padding:16px; }
`;

function DisclaimerSection() {
  return (
    <DiscWrap>
      <DiscBox>
        <FaExclamationTriangle className="di" aria-hidden="true" />
        <div>
          <h3>Important Disclaimer</h3>
          <p>
            Trend Genie provides market analysis and educational insights only. It does not
            constitute financial advice, investment advice, or trading recommendations.
            Cryptocurrency markets are highly volatile and carry significant risk of loss.
            Always conduct your own research and consult a qualified financial adviser before
            making any trading or investment decisions. Past performance of any analysis is
            not indicative of future results.
          </p>
        </div>
      </DiscBox>
    </DiscWrap>
  );
}

/* ═══════════════════════════════════════════════════════
   PAGE — schema + assembly
═══════════════════════════════════════════════════════ */

const PageWrap = styled.div``;

const schemaApp = {
  '@context':'https://schema.org','@type':'MobileApplication',
  name:'Trend Genie — AI Crypto Market Analysis',
  operatingSystem:'Android, iOS', applicationCategory:'FinanceApplication',
  aggregateRating:{ '@type':'AggregateRating', ratingValue:'5', ratingCount:'104' },
  offers:{ '@type':'Offer', price:'0', priceCurrency:'USD' },
  description:'Trend Genie is an AI-powered crypto market analysis app that helps users understand cryptocurrency trends, analyze trading signals, and make more informed market decisions. Available on Android and iOS.',
  image:['https://www.trendgenie.io/social-share-image.png'],
  url:'https://www.trendgenie.io', downloadUrl:APP_LINKS.googlePlay,
  author:{ '@type':'Organization', name:'Trend Genie', url:'https://www.trendgenie.io' },
};

const schemaOrg = {
  '@context':'https://schema.org','@type':'Organization',
  name:'Trend Genie', url:'https://www.trendgenie.io',
  logo:'https://www.trendgenie.io/logo.png', sameAs:['https://x.com/trendgenie_ai'],
  description:'Trend Genie is an AI-powered crypto market analysis platform helping users understand cryptocurrency trends, trading signals, and market insights on Android and iOS.',
};

const schemaFAQ = {
  '@context':'https://schema.org','@type':'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type':'Question', name:f.q,
    acceptedAnswer:{ '@type':'Answer', text:f.a },
  })),
};

const HomePage = () => (
  <PageWrap>
    <Helmet>
      <title>Trend Genie | AI Crypto Market Analysis App for Android &amp; iOS</title>
      <meta name="description" content="Trend Genie is an AI-powered crypto market analysis app available on Android and iOS. Understand crypto trends, review trading signal insights, and make more informed market decisions. Free to download." />
      <meta name="keywords" content="AI crypto analysis, crypto market analysis, crypto trading signals, crypto trend prediction, AI trading assistant, crypto chart analysis, market trend analysis, Bitcoin market analysis, altcoin analysis, crypto signals app, AI-powered trading insights, crypto price prediction tool, technical analysis app, crypto market scanner, real-time crypto insights, Trend Genie" />
      <link rel="canonical" href="https://www.trendgenie.io/" />
      <meta property="og:title" content="Trend Genie | AI Crypto Market Analysis — Android &amp; iOS" />
      <meta property="og:description" content="Cut through the crypto noise. Trend Genie uses AI to deliver clear market analysis, trend detection, and signal insights — free on Android and iOS." />
      <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
      <meta property="og:url" content="https://www.trendgenie.io/" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Trend Genie" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@trendgenie_ai" />
      <meta name="twitter:title" content="Trend Genie | AI Crypto Market Analysis App" />
      <meta name="twitter:description" content="AI-powered crypto market analysis for Android and iOS. Understand trends, review signal insights, and make more informed decisions. Free to download." />
      <meta name="twitter:image" content="https://www.trendgenie.io/social-share-image.png" />
      <meta name="theme-color" content="#ffffff" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <script type="application/ld+json">{JSON.stringify(schemaApp)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaFAQ)}</script>
    </Helmet>

    <HomeHero />
    <ClosedSignalsTicker />
    <ActiveSignalsTicker />
    <LiveResultsBanner />
    <ProblemSection />
    <SolutionSection />
    <FeaturesSection />
    <HowItWorksSection />
    <DownloadCTASection />
    <WhyChooseSection />
    <FAQSection />
    <DisclaimerSection />
    <TokenTicker />
  </PageWrap>
);

export default HomePage;
